/**
 * WildAreaCard — GO Wild Area 2026 at the top of the Profile spine.
 *
 * Shows from Sunday 8 Nov 6 pm local (Monday's plan) until Sunday 15 Nov
 * 6 pm local, then renders nothing: a week's card, not a fixture. Built as
 * drawn on the Design canvas (2026-10-19 owner approval), then stretched to
 * the whole week (2026-10-20 owner ask: "the entire week prior, each day's
 * raid hour and the fusion / Dynamax bosses"). A seven-day strip heads every
 * view. Monday to Friday (Into the Wild) draw the day's one hour that
 * matters, 6 to 7 pm (Zacian and Zamazenta Raid Hours, then Registeel,
 * Regidrago, Latias and Latios Max Battle Hours), with a countdown, the day's
 * raids and Max Battles as tiles, what is out all week, the GO Pass's Dynamax
 * Meltan and the week's bonuses. Saturday and Sunday keep the weekend shape,
 * top to bottom:
 *
 *   - heading row with the Ticket switch (per device; flips passes, Max
 *     Particles, snapshots and lights the ticket-only bonuses);
 *   - the hour strip: eight one-hour segments, Steel and Dragons taking turns,
 *     the live hour lit, minutes left and the next habitat named;
 *   - the live habitat's species as gap tiles, your gaps first, the ones you
 *     have behind a chip. Same tile and choose-then-commit mechanic as the
 *     catch-up card and the radar strips (CatchUpTile + useGapCommit), so a
 *     tap here writes the same rows a tap anywhere else does;
 *   - the next habitat as a mini strip, today's raids and Max Battles with
 *     tier captions, free passes and the Max Particle cap, today's Mighty
 *     Pokémon, 10 km eggs, snapshot ticks, bonus pills, and the other day's
 *     line-up as a strip that flips the card to that day.
 *
 * Everything the card says comes from lib/wildArea2026 (curated, sourced) and
 * the trainer's own dex state. It does not read the live feed: the feed has
 * the event but none of its shape, and the show window is the event's own.
 */
import { useEffect, useMemo, useState, type ReactNode } from "react";
import {
  Camera,
  Check,
  Clock,
  Compass,
  Egg,
  Gift,
  Layers,
  Mountain,
  Swords,
  Ticket,
  CalendarDays,
  ChevronRight,
} from "lucide-react";
import { InfoBlock } from "@/components/InfoBlock";
import { CatchUpTile, GAP_TILE_CELL_STYLE, GAP_TILE_ROW_CLASS } from "@/components/EventCatchUp";
import { CaughtSprite } from "@/components/CaughtSprite";
import { PokemonSilhouette } from "@/components/PokemonSilhouette";
import { BonusPill } from "@/components/BonusBadges";
import { useGapCommit } from "@/hooks/useGapCommit";
import { parseBonusText } from "@/lib/eventBonuses";
import { bindDexSyncApiFetch, DEX_STATE_EVENT, getDexEntryState } from "@/lib/dexStateStore";
import { apiRequestRaw } from "@/lib/queryClient";
import { speciesFormArtCandidates } from "@/lib/formSpriteCandidates";
import { speciesName } from "@/data/speciesNames";
import type { RaidDexVariant } from "@/lib/raidDexCrossRef";
import {
  WILD_AREA_2026,
  WILD_AREA_ALL_DAYS,
  WILD_AREA_DAYS,
  WILD_AREA_OPEN_HOUR,
  WILD_AREA_PREFS_EVENT,
  WILD_AREA_WEEK_DAYS,
  readWildAreaSnapshots,
  readWildAreaTicket,
  wildAreaAt,
  wildAreaDay,
  wildAreaDayLabel,
  wildAreaDayNumber,
  wildAreaFirstHabitat,
  wildAreaGaps,
  wildAreaHabitat,
  wildAreaHourLabel,
  wildAreaHourStrip,
  wildAreaLocalMs,
  wildAreaNextDay,
  wildAreaOwned,
  wildAreaPrevDay,
  wildAreaWeekdayLabel,
  writeWildAreaSnapshots,
  writeWildAreaTicket,
  type WildAreaGap,
  type WildAreaHabitat,
  type WildAreaHourState,
  type WildAreaRaidBoss,
  type WildAreaSpecies,
} from "@/lib/wildArea2026";

const isPresent = (variant: RaidDexVariant, speciesId: number, formId?: string): boolean =>
  getDexEntryState(variant, speciesId, formId ?? null)?.state === "present";

/** Main before Shiny before the Max dexes, the badge-colour grouping every gap strip uses. */
const VARIANT_ORDER: Partial<Record<RaidDexVariant, number>> = { pokemon: 0, shiny: 1, dynamax: 2, gmax: 3 };
const byVariant = (a: WildAreaGap, b: WildAreaGap) =>
  (VARIANT_ORDER[a.variant] ?? 9) - (VARIANT_ORDER[b.variant] ?? 9);

/** Rows of five gap tiles shown before the "Show N more" chip. */
const GAP_ROWS_SHOWN = 2;

const gapTestId = (prefix: string, g: WildAreaGap) =>
  `${prefix}-${g.variant}-${g.speciesId}${g.formId ? `-${g.formId}` : ""}`;

function useNow(): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(t);
  }, []);
  return now;
}

/** "25 min", "1 h 20 min", "18 h". */
function spanLabel(ms: number): string {
  const mins = Math.max(1, Math.ceil(ms / 60_000));
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h >= 6 || m === 0) return `${h} h`;
  return `${h} h ${m} min`;
}

export function WildAreaCard({ className }: { className?: string }) {
  const now = useNow();
  const at = useMemo(() => wildAreaAt(now), [now]);
  const [pickedDay, setPickedDay] = useState<string | null>(null);
  const [ticket, setTicket] = useState<boolean>(() => readWildAreaTicket());
  const [prefsTick, setPrefsTick] = useState(0);
  const [dexTick, setDexTick] = useState(0);

  // Commits here must reach the server backup even when the Pokédex page has
  // not mounted this session (idempotent; the Shiny Hunt does the same).
  useEffect(() => {
    bindDexSyncApiFetch(apiRequestRaw);
  }, []);
  useEffect(() => {
    const onPrefs = () => {
      setTicket(readWildAreaTicket());
      setPrefsTick((t) => t + 1);
    };
    const onDex = () => setDexTick((t) => t + 1);
    window.addEventListener(WILD_AREA_PREFS_EVENT, onPrefs);
    window.addEventListener(DEX_STATE_EVENT, onDex);
    return () => {
      window.removeEventListener(WILD_AREA_PREFS_EVENT, onPrefs);
      window.removeEventListener(DEX_STATE_EVENT, onDex);
    };
  }, []);

  if (!at.visible || !at.focusDay) return null;

  const focusDay = at.focusDay;
  const viewDay = pickedDay && WILD_AREA_ALL_DAYS.includes(pickedDay) ? pickedDay : focusDay;
  const viewingFocus = viewDay === focusDay;
  const dayPlan = wildAreaDay(viewDay);
  if (!dayPlan) return null;
  const isMain = dayPlan.phase === "main";
  const live = isMain && at.live && viewingFocus;
  // The strip at the bottom points a day ahead, or back to Saturday on Sunday.
  const stripDay = wildAreaNextDay(viewDay) ?? wildAreaPrevDay(viewDay);
  const stripPlan = stripDay ? wildAreaDay(stripDay) : null;
  const allowance = ticket ? WILD_AREA_2026.ticket : WILD_AREA_2026.free;
  const pickDay = (day: string) => setPickedDay(day === focusDay ? null : day);

  // ── Weekend: the habitat the big strip features, the live one else the
  // one that opens the day; the mini strip below it shows the other.
  const current = live ? at.rotation.current : null;
  const next = live ? at.rotation.next : null;
  const featured: WildAreaHabitat =
    (current && wildAreaHabitat(current.habitat.key)) ?? wildAreaFirstHabitat();
  const other: WildAreaHabitat | null =
    WILD_AREA_2026.habitats.find((h) => h.key !== featured.key) ?? null;
  const nextShort = next ? wildAreaHabitat(next.habitat.key)?.short ?? next.habitat.name : null;
  const nextLabel = next && nextShort
    ? `${nextShort} at ${wildAreaHourLabel(next.window.startHour)}`
    : null;

  // ── Lead week: the day's hour and where it stands. A day being browsed
  // that is not today reads as "before" ahead of today and "after" behind it.
  const hour = dayPlan.hour ?? null;
  const hourState: WildAreaHourState | null = hour
    ? viewingFocus && at.hour
      ? at.hour.state
      : viewDay < focusDay
        ? "after"
        : "before"
    : null;
  const hourStartsAt = hour ? wildAreaLocalMs(viewDay, hour.startHour) : 0;
  const hourEndsAt = hour ? wildAreaLocalMs(viewDay, hour.endHour) : 0;
  const hourNames = hour ? hour.bosses.map((b) => speciesName(b.speciesId)).join(" and ") : "";
  const hourSpan = hour ? `${wildAreaHourLabel(hour.startHour)} to ${wildAreaHourLabel(hour.endHour)}` : "";

  const subtitle = isMain
    ? live && current
      ? `${wildAreaHabitat(current.habitat.key)?.short ?? current.habitat.name} on now${nextLabel ? `, ${nextLabel}` : ""}`
      : viewingFocus
        ? `Opens ${wildAreaDayLabel(focusDay)} at ${wildAreaHourLabel(WILD_AREA_OPEN_HOUR)}, Steel first`
        : `${wildAreaDayLabel(viewDay)}: Steel first at ${wildAreaHourLabel(WILD_AREA_OPEN_HOUR)}`
    : hour
      ? hourState === "live"
        ? `${hourNames} ${hour.label} on now, until ${wildAreaHourLabel(hour.endHour)}`
        : hourState === "before"
          ? `Into the Wild: ${hourNames} ${hour.label} at ${wildAreaHourLabel(hour.startHour)}`
          : `Into the Wild: today's ${hour.label} is done`
      : "Into the Wild";

  const countdown = isMain
    ? live && current
      ? { big: spanLabel(current.endsAt - now), small: "left" }
      : viewingFocus && at.opensAt
        ? { big: spanLabel(at.opensAt - now), small: "until the gates open" }
        : { big: wildAreaDayLabel(viewDay), small: "Steel first, then Dragons" }
    : hourState === "live"
      ? { big: spanLabel(hourEndsAt - now), small: "left" }
      : hourState === "before" && viewingFocus
        ? { big: spanLabel(hourStartsAt - now), small: hourSpan }
        : { big: hourSpan, small: hourState === "after" ? "done" : "local time" };

  const dayIndex = isMain ? WILD_AREA_DAYS.indexOf(viewDay) : WILD_AREA_WEEK_DAYS.indexOf(viewDay);
  const dayCount = isMain ? WILD_AREA_DAYS.length : WILD_AREA_WEEK_DAYS.length;
  const dayLine = `${isMain ? "GO Wild Area" : "Into the Wild"} · ${wildAreaDayLabel(viewDay)} · Day ${dayIndex + 1} of ${dayCount}`;

  return (
    <section className={className} data-testid="section-wild-area">
      <InfoBlock
        icon={Compass}
        title="GO Wild Area"
        subtitle={subtitle}
        data-testid="profile-wild-area"
        dataAttrs={{
          "data-day": viewDay,
          "data-phase": dayPlan.phase,
          "data-live": live ? "true" : "false",
          ...(hourState ? { "data-hour-state": hourState } : {}),
        }}
        trailing={
          <button
            type="button"
            onClick={() => writeWildAreaTicket(!ticket)}
            aria-pressed={ticket}
            aria-label={ticket ? "I have a ticket. Tap if you do not" : "I have a ticket"}
            data-testid="wild-area-ticket"
            className="inline-flex items-center gap-1 rounded-full px-2.5 text-[10px] font-extrabold"
            style={{
              minHeight: 30,
              background: ticket ? "var(--accent-fill-glow-faint)" : "var(--surface-sunken)",
              border: `1px solid ${ticket ? "var(--accent-fill-glow)" : "var(--border-subtle)"}`,
              color: ticket ? "var(--text-primary)" : "var(--text-secondary)",
            }}
          >
            <Ticket className="w-3 h-3" aria-hidden />
            Ticket
          </button>
        }
      >
        <div className="space-y-4">
          {/* The week, Monday to Sunday: today marked, the viewed day filled. */}
          <WeekStrip viewDay={viewDay} focusDay={focusDay} onPick={pickDay} />

          {/* Day line + headline + countdown (+ the hour strip on a main day) */}
          <div className="space-y-2" data-testid="wild-area-day" data-day={viewDay}>
            <p className="text-[9px] font-black uppercase tracking-[0.16em] text-[var(--text-tertiary)] truncate">
              {dayLine}
              {isMain ? (live ? " · now" : viewingFocus ? " · opens in" : "") : hourState === "live" ? " · now" : ""}
            </p>
            <div className="flex items-end justify-between gap-3">
              <div className="min-w-0 flex-1">
                <p
                  className="v2-display text-[20px] leading-tight"
                  // The habitat's type colour, pulled toward the ink so Steel's
                  // pale lilac still reads on a white card (light theme) and
                  // Dragon's violet still reads on a dark one.
                  style={{
                    color: live && current
                      ? `color-mix(in srgb, ${current.habitat.color} 55%, var(--text-primary))`
                      : hourState === "live"
                        ? "var(--event-color-raid)"
                        : "var(--text-primary)",
                  }}
                  data-testid="wild-area-headline"
                >
                  {isMain
                    ? live && current
                      ? current.habitat.name
                      : countdown.big
                    : hour
                      ? `${hourNames} ${hour.label}`
                      : wildAreaDayLabel(viewDay)}
                </p>
              </div>
              <div className="text-right shrink-0">
                {((live && current) || (!isMain && hour && (hourState === "live" || (hourState === "before" && viewingFocus)))) && (
                  <p className="text-[20px] font-extrabold leading-none tabular-nums whitespace-nowrap" data-testid="wild-area-minutes">
                    {countdown.big}
                  </p>
                )}
                <p className="mt-0.5 text-[9px] font-extrabold uppercase tracking-[0.1em] text-[var(--text-tertiary)] whitespace-nowrap">
                  {isMain || hourState === "live" || (hourState === "before" && viewingFocus) ? countdown.small : `${countdown.big} · ${countdown.small}`}
                </p>
              </div>
            </div>
            {isMain && <HourStrip day={viewDay} now={now} />}
          </div>

          {isMain && (
            <>
              {/* The featured habitat's gaps, then the ones you have behind a chip */}
              <HabitatGaps
                key={`${viewDay}:${featured.key}`}
                habitat={featured}
                live={live}
                dexTick={dexTick}
              />

              {/* The other habitat as a mini strip */}
              {other && (
                <div
                  className="rounded-xl p-2.5"
                  style={{
                    background: `color-mix(in srgb, ${other.color} 10%, transparent)`,
                    border: `1px solid color-mix(in srgb, ${other.color} 45%, transparent)`,
                  }}
                  data-testid="wild-area-next"
                  data-habitat={other.key}
                >
                  <div className="flex items-center justify-between gap-2 mb-1.5">
                    <SectionLine icon={Clock} color={`color-mix(in srgb, ${other.color} 70%, var(--text-primary))`}>
                      {live ? "Next" : "Then"} · {other.short} · {wildAreaHourLabel(Math.min(...other.hours))}
                    </SectionLine>
                    <GapCount species={other.species} dexTick={dexTick} color={`color-mix(in srgb, ${other.color} 70%, var(--text-primary))`} />
                  </div>
                  <MiniStrip species={other.species} dexTick={dexTick} max={6} testId="wild-area-next-strip" />
                </div>
              )}
            </>
          )}

          {/* Raids and Max Battles: the day's line-up, the hour's bosses first on a lead day */}
          <RaidStrip
            key={viewDay}
            bosses={dayPlan.raids}
            day={viewDay}
            title={isMain ? "Raids today" : "Raids and Max Battles"}
          />

          {isMain ? (
            <>
              <div
                className="flex items-center justify-between gap-3 rounded-xl px-3 py-2 surface-elev"
                data-testid="wild-area-passes"
                data-count={allowance.raidPasses}
              >
                <div className="flex items-center gap-2">
                  <div className="flex gap-[3px]" aria-hidden>
                    {Array.from({ length: allowance.raidPasses }, (_, i) => (
                      <span
                        key={i}
                        className="block rounded-[4px]"
                        style={{
                          width: 12,
                          height: 17,
                          border: "1.5px solid var(--event-color-raid)",
                          background: "color-mix(in srgb, var(--event-color-raid) 18%, transparent)",
                        }}
                      />
                    ))}
                  </div>
                  <p className="text-[10px] leading-tight text-[var(--text-secondary)] whitespace-nowrap">
                    <strong className="text-[var(--text-primary)]">{allowance.raidPasses}</strong> free passes
                    <br />
                    from Gyms
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-[15px] font-extrabold leading-none tabular-nums" style={{ color: "var(--event-color-raid)" }} data-testid="wild-area-mp">
                    {allowance.maxParticles.toLocaleString()}
                  </p>
                  <p className="mt-0.5 text-[8px] font-extrabold uppercase tracking-[0.1em] text-[var(--text-tertiary)] whitespace-nowrap">
                    Max Particles
                  </p>
                </div>
              </div>

              {/* Mighty */}
              {dayPlan.mighty && (
                <div data-testid="wild-area-mighty">
                  <div className="flex items-center justify-between gap-2 mb-1.5">
                    <SectionLine icon={Mountain}>Mighty today</SectionLine>
                    <span
                      className="text-[8px] font-black uppercase tracking-[0.1em] px-1.5 py-0.5 rounded-full whitespace-nowrap shrink-0"
                      style={{
                        background: "var(--accent-fill-glow-faint)",
                        border: "1px solid var(--accent-fill-glow)",
                        color: "var(--brand-green-on-light)",
                      }}
                    >
                      XL · XXL
                    </span>
                  </div>
                  <MiniStrip species={dayPlan.mighty} dexTick={dexTick} wrap testId="wild-area-mighty-strip" />
                </div>
              )}

              {/* Eggs + snapshots */}
              <div className="grid grid-cols-2 gap-2">
                <div className="rounded-xl p-2.5 surface-elev" data-testid="wild-area-eggs">
                  <SectionLine icon={Egg}>10 km eggs</SectionLine>
                  <div className="mt-1.5">
                    <MiniStrip species={WILD_AREA_2026.eggs10km} dexTick={dexTick} wrap testId="wild-area-eggs-strip" />
                  </div>
                  <p className="mt-1 text-[8.5px] leading-tight text-[var(--text-tertiary)]">
                    {ticket ? "More 10 km eggs with your ticket" : "All four can be shiny"}
                  </p>
                </div>
                <Snapshots day={viewDay} allowance={allowance.snapshots} prefsTick={prefsTick} />
              </div>

              {/* Bonuses */}
              <div data-testid="wild-area-bonuses">
                <SectionLine icon={Gift} color="hsl(var(--bonus))">
                  Bonuses · {wildAreaHourLabel(WILD_AREA_OPEN_HOUR)} to {wildAreaHourLabel(18)}
                </SectionLine>
                <div className="mt-1.5 flex flex-wrap gap-1.5">
                  {WILD_AREA_2026.bonusesFree.map((b) => (
                    <BonusPill key={b} bonus={parseBonusText(b)} />
                  ))}
                  {WILD_AREA_2026.bonusesTicket.map((b) => (
                    <span
                      key={b}
                      className="inline-flex max-w-full"
                      style={ticket ? undefined : { opacity: 0.45 }}
                      title={ticket ? undefined : "With a ticket"}
                      data-testid="wild-area-bonus-ticket"
                      data-on={ticket ? "true" : "false"}
                    >
                      <BonusPill bonus={parseBonusText(b)} />
                    </span>
                  ))}
                </div>
              </div>
            </>
          ) : (
            <>
              {/* Out all week + the GO Pass's Dynamax debut */}
              <div className="grid grid-cols-2 gap-2">
                <div className="rounded-xl p-2.5 surface-elev" data-testid="wild-area-spawns">
                  <SectionLine icon={Layers}>Out all week</SectionLine>
                  <div className="mt-1.5">
                    <MiniStrip species={WILD_AREA_2026.lead.spawns} dexTick={dexTick} wrap testId="wild-area-spawns-strip" />
                  </div>
                  <p className="mt-1 text-[8.5px] leading-tight text-[var(--text-tertiary)]">More often, shiny possible</p>
                </div>
                <div className="rounded-xl p-2.5 surface-elev" data-testid="wild-area-gopass">
                  <SectionLine icon={Ticket}>GO Pass</SectionLine>
                  <div className="mt-1.5">
                    <MiniStrip
                      species={WILD_AREA_2026.lead.goPass}
                      variants={WILD_AREA_2026.lead.goPass.map((b) => b.variant)}
                      dexTick={dexTick}
                      wrap
                      testId="wild-area-gopass-strip"
                    />
                  </div>
                  <p className="mt-1 text-[8.5px] leading-tight text-[var(--text-tertiary)]">Dynamax Meltan debuts on the free track</p>
                </div>
              </div>

              {/* Bonuses */}
              <div data-testid="wild-area-bonuses">
                <SectionLine icon={Gift} color="hsl(var(--bonus))">
                  Bonuses · all week
                </SectionLine>
                <div className="mt-1.5 flex flex-wrap gap-1.5">
                  {WILD_AREA_2026.lead.bonuses.map((b) => (
                    <BonusPill key={b} bonus={parseBonusText(b)} />
                  ))}
                </div>
              </div>
            </>
          )}

          {/* The next day (or Saturday, from Sunday), as a strip that flips the card to it */}
          {stripDay && stripPlan && (
            <button
              type="button"
              onClick={() => pickDay(stripDay)}
              data-testid="wild-area-other-day"
              data-day={stripDay}
              className="w-full text-left pt-3"
              style={{ borderTop: "1px solid var(--border-subtle)" }}
            >
              <div className="flex items-center justify-between gap-2 mb-1.5">
                <SectionLine icon={CalendarDays}>
                  {stripDay > viewDay ? "Tomorrow" : "Back"} ·{" "}
                  {stripPlan.hour
                    ? `${stripPlan.hour.bosses.map((b) => speciesName(b.speciesId)).join(" + ")} ${wildAreaHourLabel(stripPlan.hour.startHour)}`
                    : wildAreaDayLabel(stripDay)}
                </SectionLine>
                <span className="inline-flex items-center gap-0.5 text-[9px] font-extrabold uppercase tracking-[0.1em] text-[var(--brand-green-on-light)]">
                  Plan <ChevronRight className="w-3 h-3" aria-hidden />
                </span>
              </div>
              <MiniStrip
                species={[...stripPlan.raids, ...(stripPlan.mighty ?? [])]}
                variants={[...stripPlan.raids.map((b) => b.variant), ...(stripPlan.mighty ?? []).map(() => "pokemon" as RaidDexVariant)]}
                dexTick={dexTick}
                max={7}
                testId="wild-area-other-strip"
              />
            </button>
          )}

        </div>
      </InfoBlock>
    </section>
  );
}

/**
 * Monday to Sunday as seven pills: weekday over date, the viewed day filled,
 * today ringed. A tap views that day's plan; the lead days are plain, the
 * weekend's two carry the accent so the main event reads as the destination.
 */
function WeekStrip({
  viewDay,
  focusDay,
  onPick,
}: {
  viewDay: string;
  focusDay: string;
  onPick: (day: string) => void;
}) {
  return (
    <div className="flex gap-1" role="tablist" aria-label="Event week" data-testid="wild-area-week">
      {WILD_AREA_ALL_DAYS.map((day) => {
        const on = day === viewDay;
        const today = day === focusDay;
        const main = WILD_AREA_DAYS.includes(day);
        return (
          <button
            key={day}
            type="button"
            role="tab"
            aria-selected={on}
            onClick={() => onPick(day)}
            data-testid={`wild-area-week-${day}`}
            data-today={today ? "true" : "false"}
            className="flex-1 min-w-0 rounded-lg py-1 flex flex-col items-center"
            style={{
              minHeight: 36,
              background: on ? "var(--accent-fill)" : main ? "var(--accent-fill-glow-faint)" : "var(--surface-sunken)",
              border: `1px solid ${on ? "var(--accent-fill)" : today ? "var(--accent-fill-glow)" : main ? "var(--accent-fill-glow)" : "var(--border-subtle)"}`,
              boxShadow: today && !on ? "0 0 0 1px var(--accent-fill-glow)" : undefined,
              color: on ? "var(--text-on-accent)" : "var(--text-secondary)",
            }}
          >
            <span className="text-[8px] font-black uppercase tracking-[0.08em] leading-none">{wildAreaWeekdayLabel(day).slice(0, 3)}</span>
            <span className="mt-0.5 text-[11px] font-extrabold leading-none tabular-nums">{wildAreaDayNumber(day)}</span>
          </button>
        );
      })}
    </div>
  );
}

/** The small uppercase line a section opens with, the Shiny Hunt's own. */
function SectionLine({
  icon: Icon,
  color,
  children,
}: {
  icon: typeof Clock;
  color?: string;
  children: ReactNode;
}) {
  return (
    <p
      className="text-[10.5px] font-black uppercase tracking-[0.14em] inline-flex items-center gap-1.5 min-w-0"
      style={{ color: color ?? "var(--text-ink-faint)" }}
    >
      <Icon className="w-3 h-3 shrink-0" style={{ color: color ?? "var(--accent-fill)" }} aria-hidden />
      <span className="truncate">{children}</span>
    </p>
  );
}

/** Eight segments, the live one lit in its habitat's colour, hours underneath. */
function HourStrip({ day, now }: { day: string; now: number }) {
  const segments = wildAreaHourStrip(day, now);
  return (
    <div data-testid="wild-area-hours">
      <div className="flex gap-[3px] items-center">
        {segments.map((s) => (
          <span
            key={s.hour}
            data-testid={`wild-area-hour-${s.hour}`}
            data-live={s.live ? "true" : "false"}
            className="relative flex-1 rounded-[3px]"
            style={{
              height: 10,
              background: s.live
                ? s.habitat.color
                : `color-mix(in srgb, ${s.habitat.color} ${s.over ? 22 : 42}%, var(--surface-sunken))`,
              // A hairline in the habitat's colour: Steel's pale fill alone
              // vanishes on a white card.
              border: `1px solid color-mix(in srgb, ${s.habitat.color} ${s.over ? 35 : 65}%, transparent)`,
              boxShadow: s.live
                ? `0 0 0 2px color-mix(in srgb, ${s.habitat.color} 35%, transparent), 0 0 10px ${s.habitat.color}`
                : undefined,
            }}
            aria-label={`${wildAreaHourLabel(s.hour)}: ${s.habitat.name}${s.live ? ", on now" : ""}`}
          >
            {s.live && (
              <span
                aria-hidden
                className="absolute rounded-[1px]"
                style={{
                  top: -4,
                  bottom: -4,
                  width: 2,
                  background: "var(--text-primary)",
                  left: `${Math.min(98, Math.max(2, ((now - wildAreaLocalMs(day, s.hour)) / 3_600_000) * 100))}%`,
                }}
              />
            )}
          </span>
        ))}
      </div>
      <div className="flex justify-between px-0.5 mt-1 text-[8px] font-bold tabular-nums text-[var(--text-tertiary)]">
        {Array.from({ length: 9 }, (_, i) => {
          const h = WILD_AREA_OPEN_HOUR + i;
          return <span key={h}>{h > 12 ? h - 12 : h}</span>;
        })}
      </div>
    </div>
  );
}

/** "8 of 22 missing" for a species list, read live from the dex. */
function GapCount({
  species,
  dexTick: _dexTick,
  color,
}: {
  species: readonly WildAreaSpecies[];
  dexTick: number;
  color?: string;
}) {
  const mainGaps = wildAreaGaps(species, ["pokemon"], isPresent).length;
  return (
    <span
      className="text-[9px] font-extrabold tabular-nums shrink-0"
      style={{ color: color ?? "var(--brand-green-on-light)" }}
    >
      {mainGaps} of {species.length} missing
    </span>
  );
}

/**
 * A habitat's species as gap tiles (choose, then one commit), your gaps
 * first, the ones you already have folded behind a chip. The gap list is
 * frozen per habitat (the hook's rule): an added species stops being a gap,
 * so a live recompute would delete the tile just added and its undo with it.
 */
function HabitatGaps({
  habitat,
  live,
  dexTick: _dexTick,
}: {
  habitat: WildAreaHabitat;
  live: boolean;
  dexTick: number;
}) {
  const gaps = useMemo(
    () => wildAreaGaps(habitat.species, ["pokemon", "shiny"], isPresent).sort(byVariant),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [habitat.key],
  );
  const { tileState, tap, commit, undoAll, selectedCount, addedCount } = useGapCommit(gaps);
  const [showOwned, setShowOwned] = useState(false);
  // Two rows by default. A fresh dex is missing every spawn in both Main and
  // Shiny, 44 tiles, and nine rows of silhouettes at the top of the Profile
  // is a wall; the first two rows are the Main gaps you can go and close
  // this hour, the rest are one tap away. Gaps first, so what is on screen
  // is what matters.
  const [showAll, setShowAll] = useState(false);
  const visible = showAll ? gaps : gaps.slice(0, GAP_ROWS_SHOWN * 5);
  const hidden = gaps.length - visible.length;
  const owned = wildAreaOwned(habitat.species, "pokemon", isPresent);
  const missingSpecies = new Set(gaps.filter((g) => g.variant === "pokemon").map((g) => `${g.speciesId}:${g.formId ?? ""}`)).size;

  return (
    <div data-testid="wild-area-habitat" data-habitat={habitat.key} data-live={live ? "true" : "false"}>
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <SectionLine icon={Layers} color={`color-mix(in srgb, ${habitat.color} 70%, var(--text-primary))`}>
          {live ? "Out now" : "Up first"} · {habitat.short}{live ? "" : ` · ${wildAreaHourLabel(Math.min(...habitat.hours))}`}
        </SectionLine>
        <span className="text-[9px] font-extrabold tabular-nums shrink-0 text-[var(--brand-green-on-light)]" data-testid="wild-area-habitat-count">
          {missingSpecies} of {habitat.species.length} missing
        </span>
      </div>
      {gaps.length === 0 ? (
        <p className="text-[10px] text-[var(--text-tertiary)]" data-testid="wild-area-habitat-done">
          Every {habitat.name} spawn, shiny included, is already in your Nexus Dex.
        </p>
      ) : (
        <ul className={GAP_TILE_ROW_CLASS} data-testid="wild-area-habitat-tiles">
          {visible.map((g) => (
            <li key={gapTestId("k", g)} className="min-w-0" style={GAP_TILE_CELL_STYLE}>
              <CatchUpTile
                variant={g.variant}
                speciesId={g.speciesId}
                formId={g.formId}
                state={tileState(g)}
                onToggle={() => tap(g)}
                testId={gapTestId("wild-area-tile", g)}
              />
            </li>
          ))}
        </ul>
      )}
      {(hidden > 0 || showAll) && gaps.length > GAP_ROWS_SHOWN * 5 && (
        <div className="mt-2 flex justify-center">
          <button
            type="button"
            onClick={() => setShowAll((v) => !v)}
            aria-expanded={showAll}
            data-testid="wild-area-more"
            className="rounded-full px-3 text-[10px] font-extrabold"
            style={{
              minHeight: 30,
              background: "var(--accent-fill-glow-faint)",
              border: "1px solid var(--accent-fill-glow)",
              color: "var(--brand-green-on-light)",
            }}
          >
            {showAll ? "Show fewer" : `Show ${hidden} more`}
          </button>
        </div>
      )}
      {selectedCount > 0 && (
        <button
          type="button"
          onClick={commit}
          data-testid="wild-area-commit"
          className="mt-2 w-full rounded-full py-2 text-[12px] font-bold"
          style={{ background: "var(--accent-fill)", color: "var(--text-on-accent)", minHeight: 40 }}
        >
          Add {selectedCount} to my Nexus Dex
        </button>
      )}
      {selectedCount === 0 && addedCount > 0 && (
        <div className="mt-2 flex items-center justify-between gap-2">
          <p className="text-[10px] text-[var(--text-tertiary)]">{addedCount} added. Tap one to take it back.</p>
          <button
            type="button"
            onClick={undoAll}
            data-testid="wild-area-undo"
            className="shrink-0 rounded-full px-3 py-1 text-[10px] font-bold"
            style={{
              background: "var(--surface-sunken)",
              color: "var(--text-secondary)",
              border: "1px solid var(--border-subtle)",
              minHeight: 32,
            }}
          >
            Undo all
          </button>
        </div>
      )}
      {owned.length > 0 && (
        <div className="mt-2 flex flex-col items-center gap-2">
          <button
            type="button"
            onClick={() => setShowOwned((v) => !v)}
            aria-expanded={showOwned}
            data-testid="wild-area-fold"
            className="rounded-full px-3 text-[10px] font-extrabold"
            style={{
              minHeight: 30,
              background: "var(--surface-sunken)",
              border: "1px solid var(--border-subtle)",
              color: "var(--text-secondary)",
            }}
          >
            {showOwned ? "Hide" : "Show"} the {owned.length} you have
          </button>
          {showOwned && (
            <ul className={`${GAP_TILE_ROW_CLASS} w-full`} data-testid="wild-area-owned">
              {owned.map((s) => (
                <li key={`${s.speciesId}:${s.formId ?? ""}`} className="min-w-0" style={GAP_TILE_CELL_STYLE}>
                  <OwnedTile species={s} variant="pokemon" testId={`wild-area-owned-${s.speciesId}${s.formId ? `-${s.formId}` : ""}`} />
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

/** A species you already have: its art, dimmed, with a tick. Not a button. */
function OwnedTile({
  species,
  variant,
  caption,
  testId,
}: {
  species: WildAreaSpecies;
  variant: RaidDexVariant;
  caption?: string;
  testId: string;
}) {
  const name = speciesName(species.speciesId);
  return (
    <div
      className="relative w-full rounded-xl p-1 flex flex-col items-center"
      style={{ background: "var(--surface-raised)", border: "1px solid var(--border-subtle)", opacity: 0.6 }}
      data-testid={testId}
      data-status="owned"
      aria-label={`${name}, already in your Nexus Dex`}
    >
      <span className="relative block w-full aspect-square">
        <CaughtSprite
          id={species.speciesId}
          fill
          shiny={variant === "shiny"}
          formSpriteCandidates={speciesFormArtCandidates(species.speciesId, species.formId)}
        />
        <span
          className="absolute top-0 right-0 inline-flex items-center justify-center w-3.5 h-3.5 rounded-full"
          style={{ background: "var(--accent-fill)", color: "var(--text-on-accent)" }}
          aria-hidden
        >
          <Check className="w-2 h-2" />
        </span>
      </span>
      <span className="mt-0.5 w-full truncate text-center text-[7px] leading-tight text-[var(--text-tertiary)]">
        {caption ?? name}
      </span>
    </div>
  );
}

/**
 * Small sprites in one row, no names: a silhouette for a species you are
 * missing in `variant`, the real art dimmed for one you have, and a "+N"
 * chip for whatever does not fit. Gaps come first so the ones worth your
 * attention are the ones on screen.
 */
function MiniStrip({
  species,
  variants,
  dexTick: _dexTick,
  max = 7,
  wrap = false,
  testId,
}: {
  species: readonly WildAreaSpecies[];
  /** Per-species variant to read; Main when absent. */
  variants?: readonly RaidDexVariant[];
  dexTick: number;
  /** How many fit on one line beside the "+N" chip: seven at 30px in a 300px card. */
  max?: number;
  /** Every species, wrapping onto more lines, no chip (the nine Mighty, the four eggs). */
  wrap?: boolean;
  testId: string;
}) {
  const rows = species.map((s, i) => ({
    s,
    variant: variants?.[i] ?? ("pokemon" as RaidDexVariant),
    have: isPresent(variants?.[i] ?? "pokemon", s.speciesId, s.formId),
  }));
  const ordered = [...rows.filter((r) => !r.have), ...rows.filter((r) => r.have)];
  const shown = wrap ? ordered : ordered.slice(0, max);
  const rest = ordered.length - shown.length;
  return (
    <div className={`flex items-end gap-1 min-w-0 ${wrap ? "flex-wrap" : "overflow-hidden"}`} data-testid={testId}>
      {shown.map(({ s, have }) => (
        <span
          key={`${s.speciesId}:${s.formId ?? ""}`}
          className="relative block shrink-0"
          // 28px wrapping: nine Mighty on one 300px line; 30px when a chip
          // closes the line.
          style={{ width: wrap ? 26 : 30, height: wrap ? 26 : 30, opacity: have ? 0.45 : 1 }}
          data-testid={`${testId}-${s.speciesId}${s.formId ? `-${s.formId}` : ""}`}
          data-have={have ? "true" : "false"}
          title={speciesName(s.speciesId)}
        >
          {have ? (
            <CaughtSprite id={s.speciesId} fill formSpriteCandidates={speciesFormArtCandidates(s.speciesId, s.formId)} />
          ) : (
            <PokemonSilhouette id={s.speciesId} fill caught={false} shiny={false} />
          )}
        </span>
      ))}
      {rest > 0 && (
        <span
          className="ml-auto self-center shrink-0 rounded-full px-2 py-0.5 text-[9px] font-extrabold tabular-nums"
          style={{ background: "var(--surface-sunken)", border: "1px solid var(--border-subtle)", color: "var(--text-secondary)" }}
        >
          +{rest}
        </span>
      )}
    </div>
  );
}

/**
 * The day's raids and Max Battles, every boss on screen with its tier under
 * it: a gap tile (choose, then commit) for one you are missing in the dex
 * it tracks, a dimmed tick tile for one you have.
 */
function RaidStrip({
  bosses,
  day,
  title = "Raids today",
}: {
  bosses: readonly WildAreaRaidBoss[];
  day: string;
  title?: string;
}) {
  const gaps = useMemo<WildAreaGap[]>(
    () =>
      bosses
        .filter((b) => !isPresent(b.variant, b.speciesId, b.formId))
        .map((b) => (b.formId ? { variant: b.variant, speciesId: b.speciesId, formId: b.formId } : { variant: b.variant, speciesId: b.speciesId })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [day],
  );
  const { tileState, tap, commit, undoAll, selectedCount, addedCount } = useGapCommit(gaps);
  const gapFor = (b: WildAreaRaidBoss) =>
    gaps.find((g) => g.variant === b.variant && g.speciesId === b.speciesId && (g.formId ?? "") === (b.formId ?? ""));
  return (
    <div data-testid="wild-area-raids" data-day={day}>
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <SectionLine icon={Swords}>{title}</SectionLine>
        <span className="text-[9px] font-extrabold uppercase tracking-[0.1em] text-[var(--text-tertiary)] shrink-0 whitespace-nowrap">
          {wildAreaWeekdayLabel(day)} line-up
        </span>
      </div>
      <ul className={GAP_TILE_ROW_CLASS}>
        {bosses.map((b) => {
          const g = gapFor(b);
          const key = `${b.variant}:${b.speciesId}:${b.formId ?? ""}`;
          const testId = `wild-area-raid-${b.variant}-${b.speciesId}${b.formId ? `-${b.formId}` : ""}`;
          return (
            <li key={key} className="min-w-0 flex flex-col items-center gap-1" style={GAP_TILE_CELL_STYLE}>
              {g ? (
                <CatchUpTile
                  variant={b.variant}
                  speciesId={b.speciesId}
                  formId={b.formId}
                  state={tileState(g)}
                  onToggle={() => tap(g)}
                  testId={testId}
                />
              ) : (
                <OwnedTile species={b} variant={b.variant} testId={testId} />
              )}
              <span
                className="text-[7px] font-black uppercase tracking-[0.08em] px-1.5 py-0.5 rounded-full whitespace-nowrap"
                style={
                  b.tier === "dynamax" || b.tier === "gmax" || b.tier === "1max" || b.tier === "2max" || b.tier === "5max"
                    ? { background: "color-mix(in srgb, var(--event-color-raid) 18%, transparent)", color: "var(--event-color-raid)" }
                    : { background: "var(--surface-sunken)", color: "var(--text-secondary)", border: "1px solid var(--border-subtle)" }
                }
                data-testid={`${testId}-tier`}
              >
                {b.caption}
              </span>
            </li>
          );
        })}
      </ul>
      {selectedCount > 0 && (
        <button
          type="button"
          onClick={commit}
          data-testid="wild-area-raids-commit"
          className="mt-2 w-full rounded-full py-2 text-[12px] font-bold"
          style={{ background: "var(--accent-fill)", color: "var(--text-on-accent)", minHeight: 40 }}
        >
          Add {selectedCount} to my Nexus Dex
        </button>
      )}
      {selectedCount === 0 && addedCount > 0 && (
        <div className="mt-2 flex items-center justify-between gap-2">
          <p className="text-[10px] text-[var(--text-tertiary)]">{addedCount} added. Tap one to take it back.</p>
          <button
            type="button"
            onClick={undoAll}
            data-testid="wild-area-raids-undo"
            className="shrink-0 rounded-full px-3 py-1 text-[10px] font-bold"
            style={{
              background: "var(--surface-sunken)",
              color: "var(--text-secondary)",
              border: "1px solid var(--border-subtle)",
              minHeight: 32,
            }}
          >
            Undo all
          </button>
        </div>
      )}
    </div>
  );
}

/** GO Snapshot surprise encounters as circles you fill in as you take them. */
function Snapshots({
  day,
  allowance,
  prefsTick: _prefsTick,
}: {
  day: string;
  allowance: number;
  prefsTick: number;
}) {
  const done = Math.min(allowance, readWildAreaSnapshots(day));
  return (
    <div className="rounded-xl p-2.5 surface-elev" data-testid="wild-area-snaps" data-done={done} data-allowance={allowance}>
      <SectionLine icon={Camera}>Snapshots</SectionLine>
      <div className="mt-1.5 flex flex-wrap gap-1.5" role="group" aria-label="Snapshot encounters taken today">
        {Array.from({ length: allowance }, (_, i) => {
          const n = i + 1;
          const filled = n <= done;
          return (
            <button
              key={n}
              type="button"
              onClick={() => writeWildAreaSnapshots(day, filled && n === done ? n - 1 : n)}
              aria-pressed={filled}
              aria-label={`Snapshot encounter ${n} of ${allowance}${filled ? ", taken" : ""}`}
              data-testid={`wild-area-snap-${n}`}
              data-done={filled ? "true" : "false"}
              className="grid place-items-center rounded-full"
              style={{
                width: 26,
                height: 26,
                border: `2px solid ${filled ? "var(--accent-fill)" : "var(--border-subtle)"}`,
                background: filled ? "var(--accent-fill-glow-faint)" : "var(--surface-sunken)",
                color: "var(--accent-fill)",
              }}
            >
              {filled && <Check className="w-3 h-3" aria-hidden />}
            </button>
          );
        })}
      </div>
      <p className="mt-1 text-[8.5px] leading-tight text-[var(--text-tertiary)]">
        <strong className="text-[var(--text-secondary)]">
          {done} of {allowance}
        </strong>{" "}
        surprise encounters today. Steel and Dragon, shiny possible.
      </p>
    </div>
  );
}
